import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { ThemeProvider } from "@/theme/ThemeContext";
import AuthCallback from "@/auth/AuthCallback";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Marketplace from "@/pages/Marketplace";
import TaskDetail from "@/pages/TaskDetail";
import CreateTask from "@/pages/CreateTask";
import MyTasks from "@/pages/MyTasks";
import Approvals from "@/pages/Approvals";
import Wallet from "@/pages/Wallet";
import Leaderboard from "@/pages/Leaderboard";
import SkillMarket from "@/pages/SkillMarket";
import Analytics from "@/pages/Analytics";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import Challenges from "@/pages/Challenges";
import Rewards from "@/pages/Rewards";
import Capability from "@/pages/Capability";
import Landing from "@/pages/Landing";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SalesPortalApp from "@/sales_portal/SalesPortalApp";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground" data-testid="app-loading">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Loading WorkMesh…</div>
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  // Intercept OAuth callback at any path
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/sales-portal/*" element={<SalesPortalApp />} />
      <Route path="/" element={<Landing />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/marketplace" element={<Protected><Marketplace /></Protected>} />
      <Route path="/tasks/new" element={<Protected><CreateTask /></Protected>} />
      <Route path="/tasks/:task_id" element={<Protected><TaskDetail /></Protected>} />
      <Route path="/my-tasks" element={<Protected><MyTasks /></Protected>} />
      <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
      <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
      <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
      <Route path="/skills" element={<Protected><SkillMarket /></Protected>} />
      <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/profile/:user_id" element={<Protected><Profile /></Protected>} />
      <Route path="/challenges" element={<Protected><Challenges /></Protected>} />
      <Route path="/rewards" element={<Protected><Rewards /></Protected>} />
      <Route path="/capability" element={<Protected><Capability /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppShell />
          <Toaster
            position="bottom-right"
            toastOptions={{
              className: "rounded-sm",
            }}
          />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
